import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { v4 as uuidv4 } from 'uuid';
import type { QuestionContent, MixMatchContent, MixMatchItem, MixMatchPair } from '@/types';
import styles from './QuestionEditor.module.scss';

interface MixMatchEditorProps {
  content: QuestionContent | null;
  onChange: (content: QuestionContent) => void;
}

export function MixMatchEditor({ content, onChange }: MixMatchEditorProps) {
  const [leftColumn, setLeftColumn] = useState<MixMatchItem[]>([]);
  const [rightColumn, setRightColumn] = useState<MixMatchItem[]>([]);
  const [correctPairs, setCorrectPairs] = useState<MixMatchPair[]>([]);

  // Initialize from existing content
  useEffect(() => {
    if (content && content.type === 'mix_match') {
      setLeftColumn(content.leftColumn);
      setRightColumn(content.rightColumn);
      setCorrectPairs(content.correctPairs);
    } else {
      // Start with 3 empty pairs
      const newLeft: MixMatchItem[] = Array.from({ length: 3 }, () => ({
        id: uuidv4(),
        text: '',
      }));
      const newRight: MixMatchItem[] = Array.from({ length: 3 }, () => ({
        id: uuidv4(),
        text: '',
      }));
      setLeftColumn(newLeft);
      setRightColumn(newRight);
      setCorrectPairs([]);
    }
  }, [content]);

  // Update parent when data changes
  useEffect(() => {
    if (
      leftColumn.length >= 2 &&
      rightColumn.length >= 2 &&
      leftColumn.every((item) => item.text.trim() !== '') &&
      rightColumn.every((item) => item.text.trim() !== '') &&
      correctPairs.length >= 1
    ) {
      const newContent: MixMatchContent = {
        type: 'mix_match',
        leftColumn,
        rightColumn,
        correctPairs,
      };
      onChange(newContent);
    }
  }, [leftColumn, rightColumn, correctPairs, onChange]);

  const handleLeftTextChange = (id: string, text: string) => {
    setLeftColumn((prev) => prev.map((item) => (item.id === id ? { ...item, text } : item)));
  };

  const handleRightTextChange = (id: string, text: string) => {
    setRightColumn((prev) => prev.map((item) => (item.id === id ? { ...item, text } : item)));
  };

  const handleAddLeftItem = () => {
    setLeftColumn((prev) => [...prev, { id: uuidv4(), text: '' }]);
  };

  const handleAddRightItem = () => {
    setRightColumn((prev) => [...prev, { id: uuidv4(), text: '' }]);
  };

  const handleRemoveLeftItem = (id: string) => {
    if (leftColumn.length > 2) {
      setLeftColumn((prev) => prev.filter((item) => item.id !== id));
      // Remove any pairs involving this item
      setCorrectPairs((prev) => prev.filter((pair) => pair.leftId !== id));
    }
  };

  const handleRemoveRightItem = (id: string) => {
    if (rightColumn.length > 2) {
      setRightColumn((prev) => prev.filter((item) => item.id !== id));
      // Remove any pairs involving this item
      setCorrectPairs((prev) => prev.filter((pair) => pair.rightId !== id));
    }
  };

  const handlePairChange = (leftId: string, rightId: string) => {
    // Check if this left item is already paired
    const existingPairIndex = correctPairs.findIndex((pair) => pair.leftId === leftId);

    if (rightId === '') {
      // Remove the pair if right is empty
      if (existingPairIndex !== -1) {
        setCorrectPairs((prev) => prev.filter((_, i) => i !== existingPairIndex));
      }
    } else {
      // Update or add the pair
      if (existingPairIndex !== -1) {
        setCorrectPairs((prev) =>
          prev.map((pair, i) =>
            i === existingPairIndex ? { leftId, rightId } : pair
          )
        );
      } else {
        setCorrectPairs((prev) => [...prev, { leftId, rightId }]);
      }
    }
  };

  const getPairedRightId = (leftId: string): string => {
    const pair = correctPairs.find((p) => p.leftId === leftId);
    return pair ? pair.rightId : '';
  };

  return (
    <div className={styles.editor}>
      <h4 className={styles.editor__heading}>Mix and Match Pairs</h4>

      <div className={styles.editor__info}>
        Create matching pairs by adding items to both columns, then specify which items match.
      </div>

      <div className={styles.editor__grid}>
        {/* Left Column */}
        <div className={styles.editor__column}>
          <h5 className={styles.editor__columnTitle}>Left Column</h5>
          <div className={styles.editor__optionsList}>
            <AnimatePresence>
              {leftColumn.map((item, index) => (
                <motion.div
                  key={item.id}
                  className={styles.editor__option}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.05 }}
                  layout
                >
                  <div className={styles.editor__optionHeader}>
                    <span className={styles.editor__optionNumber}>{index + 1}</span>
                  </div>
                  <div className={styles.editor__optionContent}>
                    <input
                      type="text"
                      className={styles.editor__input}
                      value={item.text}
                      onChange={(e) => handleLeftTextChange(item.id, e.target.value)}
                      placeholder={`Left item ${index + 1}`}
                      maxLength={100}
                    />
                    {leftColumn.length > 2 && (
                      <motion.button
                        className={styles.editor__removeButton}
                        onClick={() => handleRemoveLeftItem(item.id)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        ×
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          <motion.button
            className={styles.editor__addButton}
            onClick={handleAddLeftItem}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            + Add Left Item
          </motion.button>
        </div>

        {/* Right Column */}
        <div className={styles.editor__column}>
          <h5 className={styles.editor__columnTitle}>Right Column</h5>
          <div className={styles.editor__optionsList}>
            <AnimatePresence>
              {rightColumn.map((item, index) => (
                <motion.div
                  key={item.id}
                  className={styles.editor__option}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  layout
                >
                  <div className={styles.editor__optionHeader}>
                    <span className={styles.editor__optionNumber}>{index + 1}</span>
                  </div>
                  <div className={styles.editor__optionContent}>
                    <input
                      type="text"
                      className={styles.editor__input}
                      value={item.text}
                      onChange={(e) => handleRightTextChange(item.id, e.target.value)}
                      placeholder={`Right item ${index + 1}`}
                      maxLength={100}
                    />
                    {rightColumn.length > 2 && (
                      <motion.button
                        className={styles.editor__removeButton}
                        onClick={() => handleRemoveRightItem(item.id)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        ×
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          <motion.button
            className={styles.editor__addButton}
            onClick={handleAddRightItem}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            + Add Right Item
          </motion.button>
        </div>
      </div>

      {/* Pair Matching */}
      <div className={styles.editor__field}>
        <h5 className={styles.editor__heading}>Define Correct Pairs</h5>
        <div className={styles.editor__optionsList}>
          {leftColumn.map((leftItem, index) => (
            <div key={leftItem.id} className={styles.editor__option}>
              <div className={styles.editor__item}>
                <span className={styles.editor__itemNumber}>{index + 1}</span>
                <span>{leftItem.text || `Left ${index + 1}`}</span>
              </div>
              <span style={{ margin: '0 8px' }}>→</span>
              <select
                className={styles.editor__input}
                value={getPairedRightId(leftItem.id)}
                onChange={(e) => handlePairChange(leftItem.id, e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">-- Select match --</option>
                {rightColumn.map((rightItem, rIndex) => (
                  <option key={rightItem.id} value={rightItem.id}>
                    {rightItem.text || `Right ${rIndex + 1}`}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      {correctPairs.length === 0 && leftColumn.some((item) => item.text.trim() !== '') && (
        <p className={styles.editor__warning}>
          ⚠️ At least one correct pair must be defined
        </p>
      )}
    </div>
  );
}
